import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { API_ENDPOINTS } from 'Constants/env';
import { useNotificationStore } from 'Modules/notification/store';
import { VIDEO_HISTORY_NETWORK_ERROR } from 'Modules/videoHistory/constants/networkError.constants';
import { summaryResponseToModel } from 'Modules/videoHistory/resolvers/summary';
import { transcriptionResponseToModel } from 'Modules/videoHistory/resolvers/transcriptions';
import {
  IFetchSummaryResult,
  ISummaryResponse,
  IRegenerateSummaryResponse,
} from 'Modules/videoHistory/types/summary.types';
import {
  IFetchTranscriptionsResult,
  ITranscriptionResponse,
} from 'Modules/videoHistory/types/transcriptions.types';
import { isNetworkFetchResult, isSummary } from 'Modules/videoHistory/utils';
import {
  isBrowserOffline,
  getHttpErrorStatus,
  shouldTreatFetchFailureAsNetwork,
} from 'Modules/videoHistory/utils/isNetworkTransportError';
import {
  ANALYTICS_EVENT_SUMMARY_REGENERATION_FAILED,
  ANALYTICS_EVENT_SUMMARY_REGENERATION_SUCCESS,
  pushToGTMDataLayer,
} from 'Utils/analytics';
import API from '~/api';
import { resolveFetchError } from './resolveFetchError';

// Query keys
export const QUERY_KEY_FETCH_TRANSCRIPTIONS =
  'videoHistory.fetchTranscriptions';
export const QUERY_KEY_DELETE_TRANSCRIPTION =
  'videoHistory.deleteTranscription';
export const QUERY_KEY_FETCH_SUMMARY =
  'videoHistory.fetchSummary';
export const QUERY_KEY_REGENERATE_SUMMARY =
  'videoHistory.regenerateSummary';

export const VIDEO_HISTORY_POLL_INTERVAL_MS = 5000;
export const DEFAULT_TRANSCRIPTIONS_PAGE_SIZE = 10;
export const TRANSCRIPTIONS_INITIAL_CURSOR = 1;

export type FetchTranscriptionsParams = {
  cursor: number;
  limit: number;
};

type FetchTranscriptionsOptions = {
  suppressErrorToast?: boolean;
  /**
   * Throw on failure for subsequent pages instead of resolving to `null` or
   * the network error sentinel.
   *
   * A resolved failure is treated as a successful page by React Query, which
   * can cause `getNextPageParam` to stop pagination. Throwing keeps the last
   * successful page as the pagination state so the next scroll can retry the
   * failed request (CUU2-3514).
   *
   * A 404 is still treated as the end of the transcription, not as an error.
   */
  throwOnFailure?: boolean;
};

export const fetchTranscriptions = async (
  conferenceId: number,
  { cursor, limit }: FetchTranscriptionsParams,
  {
    suppressErrorToast = false,
    throwOnFailure = false,
  }: FetchTranscriptionsOptions = {}
): Promise<IFetchTranscriptionsResult> => {
  if (isBrowserOffline()) {
    if (throwOnFailure) {
      throw new Error('fetchTranscriptions: the browser is offline');
    }

    return VIDEO_HISTORY_NETWORK_ERROR;
  }

  try {
    const response = await API.get<ITranscriptionResponse>(
      API_ENDPOINTS.FetchTranscriptions(conferenceId),
      { params: { includeSegments: true, cursor, limit } }
    );

    const { transcript } = response.data;

    // transcript AVAILABLE with no segments, same as 404 (empty state, no error toast)
    if (
      transcript.status === 'AVAILABLE' &&
      transcript.segmentCount === 0 &&
      (transcript.segments?.length ?? 0) === 0
    ) {
      return null;
    }

    return transcriptionResponseToModel(response.data);
  } catch (error) {
    if (throwOnFailure && getHttpErrorStatus(error) !== 404) {
      throw error;
    }

    return resolveFetchError(error, VIDEO_HISTORY_NETWORK_ERROR, {
      suppressErrorToast,
      toastTitle: 'Error fetching transcription',
      toastMessage: "We couldn't fetch the transcription. Please try again",
    });
  }
};

export const deleteTranscription = async (conferenceId: number) => {
  try {
    await API.delete(API_ENDPOINTS.FetchTranscriptions(conferenceId));

    return true;
  } catch (error) {
    // 409 means the transcription is already pending deletion
    // Treat this as a success; no error toast is needed
    if (getHttpErrorStatus(error) === 409) {
      return true;
    }

    useNotificationStore.getState().addNotify({
      type: 'error',
      dismissible: true,
      title: 'Error deleting transcription',
      message: "We couldn't delete the transcription. Please try again",
    });

    return false;
  }
};

export type UseFetchTranscriptionsOptions = {
  limit?: number;
  enabled?: boolean;
};

export const useFetchTranscriptions = (
  conferenceId?: number,
  {
    limit = DEFAULT_TRANSCRIPTIONS_PAGE_SIZE,
    enabled = true,
  }: UseFetchTranscriptionsOptions = {}
) => {
  const queryClient = useQueryClient();
  const queryKey = [
    QUERY_KEY_FETCH_TRANSCRIPTIONS,
    conferenceId ?? 'none',
    limit,
  ] as const;

  return useInfiniteQuery({
    queryKey,
    queryFn: ({ pageParam }) => {
      if (!conferenceId) {
        return Promise.reject(
          new Error('useFetchTranscriptions: conferenceId is required')
        );
      }

      const hasCachedData = Boolean(
        queryClient.getQueryData<{ pages: unknown[] }>(queryKey)?.pages?.length
      );

      return fetchTranscriptions(
        conferenceId,
        { cursor: pageParam, limit },
        {
          suppressErrorToast: hasCachedData,
          throwOnFailure: pageParam !== TRANSCRIPTIONS_INITIAL_CURSOR,
        }
      );
    },
    initialPageParam: TRANSCRIPTIONS_INITIAL_CURSOR,
    getNextPageParam: (lastPage) => {
      if (!lastPage || isNetworkFetchResult(lastPage)) {
        return undefined;
      }
      if (!lastPage.nextCursor) {
        return undefined;
      }
      return lastPage.nextCursor;
    },
    enabled: enabled && !!conferenceId,
    refetchOnWindowFocus: false,
    refetchInterval: (query) =>
      isNetworkFetchResult(query.state.data?.pages?.[0])
        ? VIDEO_HISTORY_POLL_INTERVAL_MS
        : false,
    refetchIntervalInBackground: false,
  });
};

export const useDeleteTranscription = (conferenceId?: number) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: [QUERY_KEY_DELETE_TRANSCRIPTION, conferenceId ?? 'none'],
    mutationFn: () => {
      if (!conferenceId) {
        return Promise.reject(
          new Error('useDeleteTranscription: conferenceId is required')
        );
      }

      return deleteTranscription(conferenceId);
    },
    onSuccess: (deleted) => {
      if (!deleted || !conferenceId) {
        return;
      }

      void queryClient.invalidateQueries({
        queryKey: [QUERY_KEY_FETCH_TRANSCRIPTIONS, conferenceId],
      });
      void queryClient.invalidateQueries({
        queryKey: [QUERY_KEY_FETCH_SUMMARY, conferenceId],
      });
    },
  });
};


type FetchSummaryOptions = {
  suppressErrorToast?: boolean;
};

export const fetchSummary = async (
  participantId: number,
  { suppressErrorToast = false }: FetchSummaryOptions = {}
): Promise<IFetchSummaryResult> => {
  if (isBrowserOffline()) {
    return VIDEO_HISTORY_NETWORK_ERROR;
  }

  try {
    const response = await API.get<ISummaryResponse>(
      API_ENDPOINTS.FetchSummary(participantId),
      { params: { format: 'markdown' } }
    );
    return summaryResponseToModel(response.data);
  } catch (error) {
    return resolveFetchError(error, VIDEO_HISTORY_NETWORK_ERROR, {
      suppressErrorToast,
      toastTitle: 'Error fetching summary',
      toastMessage: "We couldn't fetch the summary. Please try again",
    });
  }
};

export const regenerateSummary = async (
  participantId: number
): Promise<IRegenerateSummaryResponse | boolean> => {
  try {
    const response = await API.post<IRegenerateSummaryResponse>(
      API_ENDPOINTS.RegenerateSummary(participantId)
    );
    return response.data;
  } catch (error) {
    if (shouldTreatFetchFailureAsNetwork(error)) {
      throw error;
    }

    // 409 means the summary cannot be regenerated because it is not in a failed state
    // In this case, show the existing summary instead of displaying an error toast
    if (getHttpErrorStatus(error) === 409) {
      return true;
    }

    useNotificationStore.getState().addNotify({
      type: 'error',
      dismissible: true,
      title: 'Error regenerating summary',
      message: "We couldn't regenerate the summary. Please try again",
    });
    return false;
  }
};

export type UseFetchSummaryOptions = {
  enabled?: boolean;
};

const getSummaryPollIntervalMs = (
  data: IFetchSummaryResult | undefined
): number | false => {
  if (isNetworkFetchResult(data)) {
    return VIDEO_HISTORY_POLL_INTERVAL_MS;
  }

  if (!isSummary(data)) {
    return false;
  }

  if (
    data.status === 'PENDING' ||
    data.status === 'PROCESSING' ||
    data.status === 'FAILED'
  ) {
    return VIDEO_HISTORY_POLL_INTERVAL_MS;
  }

  return false;
};

export const useFetchSummary = (
  participantId?: number,
  { enabled = true }: UseFetchSummaryOptions = {}
) => {
  const queryClient = useQueryClient();
  const queryKey = [QUERY_KEY_FETCH_SUMMARY, participantId ?? 'none'] as const;

  return useQuery({
    queryKey,
    queryFn: () => {
      if (!participantId) {
        return Promise.reject(
          new Error('useFetchSummary: participantId is required')
        );
      }

      const hasCachedData = queryClient.getQueryData(queryKey) !== undefined;

      return fetchSummary(participantId, {
        suppressErrorToast: hasCachedData,
      });
    },
    enabled: enabled && !!participantId,
    refetchInterval: (query) => getSummaryPollIntervalMs(query.state.data),
    refetchIntervalInBackground: false,
  });
};

export const useRegenerateSummary = (participantId?: number) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: [QUERY_KEY_REGENERATE_SUMMARY, participantId ?? 'none'],
    mutationFn: () => {
      if (!participantId) {
        return Promise.reject(
          new Error('useRegenerateSummary: participantId is required')
        );
      }
      return regenerateSummary(participantId);
    },
    onSuccess: (result) => {
      if (result === false) {
        pushToGTMDataLayer(ANALYTICS_EVENT_SUMMARY_REGENERATION_FAILED, {
          conferenceId: participantId,
        });
        return;
      }

      void queryClient.invalidateQueries({
        queryKey: [QUERY_KEY_FETCH_SUMMARY, participantId],
      });

      if (result === true) {
        // 409: summary exists but is not in a FAILED state; just sync UI with current summary state
        return;
      }

      pushToGTMDataLayer(ANALYTICS_EVENT_SUMMARY_REGENERATION_SUCCESS, {
        conferenceId: participantId,
      });
    },
    onError: (error) => {
      if (shouldTreatFetchFailureAsNetwork(error)) {
        return;
      }

      pushToGTMDataLayer(ANALYTICS_EVENT_SUMMARY_REGENERATION_FAILED, {
        conferenceId: participantId,
      });
    },
  });
};